import { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Input, Spin, Empty, InputRef, Tag } from "antd";
import {
  SearchOutlined,
  LoadingOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  visitNotesController,
  hpiTemplateController,
} from "../../../AppController";
import { useAppSelector } from "../../../hooks/redux";
import {
  getEffectiveVisitDate,
  getVisitDateTime,
  isCircadiaVisitNoteSource,
} from "../../../util/visitTimeUtils";
import { normalizeCompassusProviderName } from "../../../util/normalizeCompassusProviderName";
import { formatHomeHealthTemplateDisplayName } from "../../../util/homeHealthTemplateDisplay";
import { maskResidentName } from "../../../util/common";
import { isCompassusExperience } from "../../../util/isCompassusUser";
import { filterTemplatesByUserType } from "../../../redux/HPITemplate/hpiTemplate.slice";
import { isCompassusHhRpmClinic } from "../../../pages/CompassusNavigator/hhRpmClinics";
import styles from "./styles.module.scss";

/** API sometimes stores JSON null as the string "null" / "undefined"; hide those in the UI */
function sanitizeNoteField(value: unknown): string {
  if (value == null) return "";
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (lower === "null" || lower === "undefined") return "";
  return trimmed;
}

interface IVisitNoteSearchResult {
  noteId: number;
  patientId: number;
  patientName: string;
  visitDate: string;
  visitType: string;
  providerName: string;
  isCircadiaSource: boolean;
  noteContent: string;
  transcriptContent: string;
  prechartContent: string;
  docType: "np_visit" | "at_risk_review" | "third_party_np_visit";
  context?: Record<string, any>;
  templateId?: string | number | null;
  templateName?: string | null;
}

interface IVisitNotesSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelectNote: (note: IVisitNoteSearchResult) => void;
}

interface ISearchTemplate {
  id: number;
  name: string;
  status: number;
}

const scopedTemplateCache = new Map<string, ISearchTemplate[]>();

const VisitNotesSearchModal = ({
  open,
  onClose,
  onSelectNote,
}: IVisitNotesSearchModalProps) => {
  const { user, userClinic, betaFeaturesAllowedList } = useAppSelector(
    (state) => ({
      user: state.auth.user,
      userClinic: state.clinic.userClinic,
      betaFeaturesAllowedList: state.auth.betaFeaturesAllowedList ?? [],
    })
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [allNotes, setAllNotes] = useState<IVisitNoteSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [scopedTemplates, setScopedTemplates] = useState<ISearchTemplate[]>([]);
  const inputRef = useRef<InputRef>(null);
  const requestIdRef = useRef(0);
  const isHomeHealthExternal = isCompassusExperience({ user, userClinic });
  const isHhRpmClinic = isCompassusHhRpmClinic(
    userClinic,
    betaFeaturesAllowedList
  );
  const templateScope = isHhRpmClinic
    ? "hh-rpm"
    : isHomeHealthExternal
    ? "home-health"
    : "regular";
  const templateScopeKey = userClinic?.id
    ? `${userClinic.id}:${templateScope}`
    : null;

  // Debug flag - set to false to disable logs
  const DEBUG = true;
  const log = useCallback(
    (...args: any[]) => {
      // eslint-disable-next-line no-console
      if (DEBUG) console.log(...args);
    },
    [DEBUG]
  );

  // Log component lifecycle
  useEffect(() => {
    log("🔧 VisitNotesSearchModal mounted");
    return () => {
      log("💀 VisitNotesSearchModal unmounted");
    };
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    log("🚪 Modal open state changed:", open);
    if (open) {
      // Clear everything when opening
      log("🔄 Resetting state on modal open");
      setSearchQuery("");
      setAllNotes([]);
      setIsLoading(false);
    }
  }, [open, log]);

  // Focus the search input as soon as the modal is open. We retry a few times
  // because:
  //   - destroyOnClose remounts the input each open, so the ref isn't attached
  //     on the first tick.
  //   - antd Modal animates open over ~200-300ms; calling .focus() while the
  //     dialog is mid-transition can be ignored by the browser.
  // Modal's `afterOpenChange` (below) is the primary trigger; this effect is a
  // belt-and-suspenders fallback for the very first paint.
  const focusSearchInput = useCallback(() => {
    let attempts = 0;
    const tryFocus = () => {
      attempts += 1;
      const node = inputRef.current;
      if (node) {
        node.focus({ cursor: "end" });
        log("✅ Input focused", { attempts });
        return;
      }
      if (attempts < 10) {
        setTimeout(tryFocus, 50);
      } else {
        log("⚠️  Gave up focusing input after retries");
      }
    };
    tryFocus();
  }, [log]);

  useEffect(() => {
    if (open) {
      log("🎯 Auto-focusing input on open");
      focusSearchInput();
    }
  }, [open, focusSearchInput, log]);

  useEffect(() => {
    if (!open || !userClinic?.id || !templateScopeKey) return;

    const cachedTemplates = scopedTemplateCache.get(templateScopeKey);
    if (cachedTemplates) {
      setScopedTemplates(cachedTemplates);
      return;
    }
    setScopedTemplates([]);

    let cancelled = false;
    const fetchTemplates = async () => {
      try {
        const response = await hpiTemplateController.getHPITemplates({
          clinic_id: userClinic.id,
        });
        if (cancelled || !response.success || !response.data?.templates) return;

        const activeTemplates = response.data.templates.filter(
          (template: any) => template.status === 1
        );
        const filteredTemplates = filterTemplatesByUserType(
          activeTemplates,
          isHomeHealthExternal,
          isHhRpmClinic
        );
        scopedTemplateCache.set(templateScopeKey, filteredTemplates);
        setScopedTemplates(filteredTemplates);
      } catch (error) {
        console.error(
          "[VisitNotesSearchModal] Error fetching templates:",
          error
        );
      }
    };

    void fetchTemplates();
    return () => {
      cancelled = true;
    };
  }, [
    isHomeHealthExternal,
    isHhRpmClinic,
    open,
    templateScopeKey,
    userClinic?.id,
  ]);

  const getSearchResultTemplateName = useCallback(
    (note: IVisitNoteSearchResult): string | null => {
      if (note.templateName) {
        return formatHomeHealthTemplateDisplayName(note.templateName);
      }

      const context = note.context || {};
      const scheduleCtx = context.schedule || {};
      const explicitTemplateName =
        scheduleCtx.template_name || context.template_name;
      if (explicitTemplateName) {
        return formatHomeHealthTemplateDisplayName(explicitTemplateName);
      }

      const templateId =
        note.templateId || scheduleCtx.template_id || context.template_id;
      if (templateId && scopedTemplates.length > 0) {
        const numericTemplateId =
          typeof templateId === "string"
            ? parseInt(templateId, 10)
            : templateId;
        const matchedTemplate = scopedTemplates.find(
          (template) => template.id === numericTemplateId
        );
        if (matchedTemplate?.name) {
          return formatHomeHealthTemplateDisplayName(matchedTemplate.name);
        }
      }

      return null;
    },
    [scopedTemplates]
  );

  const getTemplateTypeTagClassName = useCallback((templateName: string) => {
    const normalized = templateName.toLowerCase();
    const variantClassName = normalized.includes("initial")
      ? styles.templateTypeTagInitial
      : /follow[\s-]*up/.test(normalized)
      ? styles.templateTypeTagFollowUp
      : styles.templateTypeTagDefault;
    return `${styles.templateTypeTag} ${variantClassName}`;
  }, []);

  const formatSearchResultVisitDate = useCallback(
    (note: IVisitNoteSearchResult): string => {
      const isCompassusSync = !note.isCircadiaSource;
      const date = getVisitDateTime(note.visitDate, isCompassusSync);
      if (!date || !date.isValid()) return "";

      if (isCompassusSync) {
        const isMidnight = date.hour() === 0 && date.minute() === 0;
        return isMidnight
          ? date.format("MM/DD/YYYY")
          : date.format("MM/DD/YYYY hh:mm a");
      }

      return date.format("MM/DD/YYYY hh:mm a");
    },
    []
  );

  const SEARCH_DEBOUNCE_MS = 1000;

  // Fetch notes via server-side search API (transcript search; date range: 2y past to 6w future)
  const fetchNotes = useCallback(
    async (searchTerm: string) => {
      if (!userClinic?.id) return;

      const thisRequestId = ++requestIdRef.current;
      log(
        "📥 Searching visit notes:",
        searchTerm ? `q="${searchTerm}"` : "(all in date range)",
        `(requestId=${thisRequestId})`
      );
      setIsLoading(true);
      try {
        const result = await visitNotesController.searchVisitNotes(
          userClinic.id,
          searchTerm.trim() || undefined,
          200
        );

        if (result.success && result.data?.patients) {
          const notes: IVisitNoteSearchResult[] = [];

          result.data.patients.forEach((patient: any) => {
            if (patient.notes && Array.isArray(patient.notes)) {
              patient.notes.forEach((note: any) => {
                if (note.doc_type === "third_party_np_visit") {
                  let noteContent = "";
                  let prechartContent = "";
                  let parsedContext: Record<string, any> | undefined;

                  try {
                    const parsedNotes =
                      typeof note.notes === "string"
                        ? JSON.parse(note.notes)
                        : note.notes;
                    noteContent = sanitizeNoteField(
                      parsedNotes?.historyOfPresentIllness
                    );
                  } catch (error) {
                    console.error("Error parsing notes:", error);
                  }

                  try {
                    parsedContext =
                      typeof note.context === "string"
                        ? JSON.parse(note.context)
                        : note.context;
                    prechartContent = sanitizeNoteField(
                      parsedContext?.prechart_note
                    );
                  } catch (error) {
                    console.error("Error parsing context:", error);
                  }

                  notes.push({
                    noteId: note.note_id,
                    patientId: patient.patient_id,
                    patientName:
                      `${patient.first_name || ""} ${
                        patient.last_name || ""
                      }`.trim() ||
                      patient.name ||
                      "Unknown",
                    visitDate: getEffectiveVisitDate(note),
                    visitType: "Third Party NP Visit",
                    providerName:
                      normalizeCompassusProviderName(
                        sanitizeNoteField(note.assigned_to_name)
                      ) ?? "",
                    isCircadiaSource: isCircadiaVisitNoteSource(note),
                    noteContent,
                    transcriptContent: "", // Server-side search; transcript not returned
                    prechartContent,
                    docType: note.doc_type,
                    context: parsedContext,
                    templateId:
                      note.template_id ||
                      parsedContext?.schedule?.template_id ||
                      parsedContext?.template_id,
                    templateName:
                      note.template_name ||
                      parsedContext?.schedule?.template_name ||
                      parsedContext?.template_name ||
                      null,
                  });
                }
              });
            }
          });

          notes.sort((a, b) => {
            const dateA = getVisitDateTime(a.visitDate, !a.isCircadiaSource);
            const dateB = getVisitDateTime(b.visitDate, !b.isCircadiaSource);
            return (dateB?.valueOf() || 0) - (dateA?.valueOf() || 0);
          });

          log("✅ Search results:", {
            count: notes.length,
            requestId: thisRequestId,
          });
          if (thisRequestId === requestIdRef.current) {
            setAllNotes(notes);
          }
        }
      } catch (error) {
        console.error("❌ Error searching visit notes:", error);
      } finally {
        if (thisRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [userClinic?.id, log]
  );

  const hasUserTypedRef = useRef(false);

  // Fetch when modal opens (all notes in date range) - runs once per open
  useEffect(() => {
    if (open && userClinic?.id) {
      hasUserTypedRef.current = false;
      fetchNotes("");
    }
  }, [open, userClinic?.id, fetchNotes]);

  // Debounced search when user types (skip initial open to avoid duplicate fetch)
  useEffect(() => {
    if (!open || !userClinic?.id) return;
    if (!hasUserTypedRef.current && searchQuery === "") return;

    hasUserTypedRef.current = true;
    const timer = setTimeout(() => {
      fetchNotes(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [open, userClinic?.id, searchQuery, fetchNotes]);

  // Server-side search returns filtered results; no client-side filtering needed
  const filteredNotes = allNotes;

  // Log whenever filteredNotes changes
  useEffect(() => {
    log("🔄 filteredNotes updated:", {
      count: filteredNotes.length,
      searchQuery,
      noteIds: filteredNotes.map((n) => n.noteId),
      hasDuplicateIds:
        filteredNotes.length !==
        new Set(filteredNotes.map((n) => n.noteId)).size,
    });
  }, [filteredNotes, searchQuery, log]);

  const handleSelectNote = (note: IVisitNoteSearchResult) => {
    onSelectNote(note);
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery("");
    onClose();
  };

  // Highlight matching text - only highlight if text contains the query
  const highlightText = (text: string, query: string) => {
    log("🎨 Highlight called:", { text: text.substring(0, 50), query });

    if (!query.trim()) return text;

    // Check if text actually contains the query (case-insensitive)
    if (!text.toLowerCase().includes(query.toLowerCase())) {
      log("⏭️  Skipping highlight - text doesn't contain query");
      return text;
    }

    // Escape special regex characters in the query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    log("🔧 Escaped query:", escapedQuery);

    try {
      const parts = text.split(new RegExp(`(${escapedQuery})`, "gi"));
      log("✂️ Split parts:", {
        partsCount: parts.length,
        parts: parts.slice(0, 5),
      });

      return (
        <span>
          {parts.map((part, index) =>
            part.toLowerCase() === query.toLowerCase() ? (
              <mark key={index} className={styles.highlight}>
                {part}
              </mark>
            ) : (
              part
            )
          )}
        </span>
      );
    } catch {
      console.error("❌ Regex failed for query:", query);
      // If regex fails, return original text
      return text;
    }
  };

  // Truncate note content for preview
  const getTruncatedContent = (content: string, maxLength: number = 150) => {
    // Remove HTML tags
    const plainText = content.replace(/<[^>]*>/g, "");
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + "...";
  };

  // Get truncated content centered around the search match
  const getTruncatedContentAroundMatch = (
    content: string,
    query: string,
    maxLength: number = 150
  ) => {
    const plainText = content.replace(/<[^>]*>/g, "");
    if (!query.trim() || plainText.length <= maxLength) {
      return plainText.length <= maxLength
        ? plainText
        : plainText.substring(0, maxLength) + "...";
    }

    const lowerText = plainText.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const matchIndex = lowerText.indexOf(lowerQuery);

    if (matchIndex === -1) {
      return plainText.length <= maxLength
        ? plainText
        : plainText.substring(0, maxLength) + "...";
    }

    // Center the match in the preview
    const halfLength = Math.floor((maxLength - query.length) / 2);
    let start = Math.max(0, matchIndex - halfLength);
    const end = Math.min(plainText.length, start + maxLength);

    // Adjust start if we're near the end
    if (end === plainText.length) {
      start = Math.max(0, end - maxLength);
    }

    let result = plainText.substring(start, end);
    if (start > 0) result = "..." + result;
    if (end < plainText.length) result = result + "...";

    return result;
  };

  // Get the best matching content to display (prioritize content that contains the search query)
  const getMatchingPreview = (
    note: IVisitNoteSearchResult,
    query: string
  ): { content: string; source: string } | null => {
    if (!query.trim()) {
      // No search query - show note content if available
      if (note.noteContent) {
        return { content: getTruncatedContent(note.noteContent), source: "" };
      }
      return null;
    }

    const lowerQuery = query.toLowerCase();

    // Check each content field for matches and return the first match with its source label
    if (note.noteContent.toLowerCase().includes(lowerQuery)) {
      return {
        content: getTruncatedContentAroundMatch(note.noteContent, query),
        source: "",
      };
    }

    if (note.prechartContent.toLowerCase().includes(lowerQuery)) {
      return {
        content: getTruncatedContentAroundMatch(note.prechartContent, query),
        source: "Prechart: ",
      };
    }

    if (note.transcriptContent.toLowerCase().includes(lowerQuery)) {
      return {
        content: getTruncatedContentAroundMatch(note.transcriptContent, query),
        source: "Transcript: ",
      };
    }

    // No content match found (match was in patient name, date, or visit type)
    // Show note content as fallback if available
    if (note.noteContent) {
      return { content: getTruncatedContent(note.noteContent), source: "" };
    }

    return null;
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={742}
      className={styles.searchModal}
      closeIcon={<CloseOutlined className={styles.closeIcon} />}
      destroyOnClose={true}
      afterOpenChange={(opened) => {
        // Fires after the open animation completes — the input is mounted
        // and the dialog is no longer transitioning, so focus actually sticks.
        if (opened) focusSearchInput();
      }}
    >
      <div className={styles.searchContainer}>
        <Input
          placeholder="Search by patient name, note content, or date..."
          prefix={<SearchOutlined className={styles.searchIcon} />}
          value={searchQuery}
          onChange={(e) => {
            const newValue = e.target.value;
            log("⌨️  Input onChange:", {
              oldValue: searchQuery,
              newValue,
              length: newValue.length,
            });
            setSearchQuery(newValue);
          }}
          className={styles.searchInput}
          ref={inputRef}
          autoFocus
        />
      </div>
      <div
        className={styles.notesList}
        key={`notes-list-${searchQuery}-${filteredNotes.length}`}
      >
        {(() => {
          if (isLoading) {
            return (
              <Spin
                spinning={isLoading}
                className={styles.loading}
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 48, color: "#161646" }}
                    spin
                  />
                }
              />
            );
          }

          if (filteredNotes.length === 0) {
            return (
              <div className={styles.emptyState}>
                <Empty
                  description={
                    searchQuery.trim()
                      ? "No matching visit notes found"
                      : "No visit notes available"
                  }
                />
              </div>
            );
          }

          log("📋 Rendering notes list", {
            count: filteredNotes.length,
            searchQuery,
            noteIds: filteredNotes.map((n) => n.noteId).slice(0, 5),
          });

          return filteredNotes.map((note) => {
            const templateName = getSearchResultTemplateName(note);
            const displayPatientName = maskResidentName(note.patientName, "3");
            return (
              <div
                key={`${note.noteId}-${note.patientId}-${note.visitDate}`}
                className={styles.noteItem}
                onClick={() => handleSelectNote(note)}
              >
                <div className={styles.noteInfo}>
                  <div className={styles.noteHeader}>
                    <span className={styles.patientName}>
                      {searchQuery.trim()
                        ? highlightText(displayPatientName, searchQuery)
                        : displayPatientName}
                    </span>
                    <span className={styles.visitDate}>
                      {formatSearchResultVisitDate(note)}
                    </span>
                  </div>
                  <div className={styles.providerRow}>
                    <span className={styles.visitType}>
                      {note.providerName
                        ? `by ${note.providerName}`
                        : "Unknown provider"}
                    </span>
                    <span className={styles.resultTags}>
                      {templateName && (
                        <Tag
                          className={getTemplateTypeTagClassName(templateName)}
                        >
                          {templateName}
                        </Tag>
                      )}
                      {note.isCircadiaSource && (
                        <Tag className={styles.circadiaSourceTag}>Circadia</Tag>
                      )}
                    </span>
                  </div>
                  {(() => {
                    const preview = getMatchingPreview(note, searchQuery);
                    if (!preview) return null;
                    return (
                      <div className={styles.notePreview}>
                        {preview.source && (
                          <span className={styles.previewSource}>
                            {preview.source}
                          </span>
                        )}
                        {searchQuery.trim()
                          ? highlightText(preview.content, searchQuery)
                          : preview.content}
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
          });
        })()}
      </div>
    </Modal>
  );
};

export default VisitNotesSearchModal;
